/**
 * Financial Aid Value Objects: Review and Students
 *
 * Section 18: Review Summary (Computed) and YST Student Entries
 *
 * @module financial-aid-domain/value-objects/financial-aid/review-students
 */
import { z } from 'zod';
import { GradeLevel, SchoolDivision } from '../../types/enums';

// ============================================================================
// Section 18: Review Summary (Computed)
// ============================================================================

export const ReviewSummarySchema = z.object({
  totalAnnualIncome: z.number().nonnegative(),
  totalAssets: z.number().nonnegative(),
  totalLiabilities: z.number().nonnegative(),
  netWorth: z.number(), // Can be negative
  totalMonthlyExpenses: z.number().nonnegative(),
  totalAnnualExpenses: z.number().nonnegative(),
  eligibleChildCount: z.number().int().nonnegative(), // Excludes preschool
  totalChildCount: z.number().int().nonnegative(),
  incomePerEligibleChild: z.number().nonnegative(),
  ystTuitionTotal: z.number().nonnegative(),
  otherTuitionTotal: z.number().nonnegative(),
  totalTuitionBurden: z.number().nonnegative(),
  tuitionAsPercentOfIncome: z.number().nonnegative(),
  debtToIncomeRatio: z.number().nonnegative(),
  expenseToIncomeRatio: z.number().nonnegative(),
});
export type ReviewSummary = z.infer<typeof ReviewSummarySchema>;

// ============================================================================
// YST Student Entry (for this financial aid application)
// ============================================================================

export const YstStudentEntrySchema = z.object({
  childApplicationId: z.string().uuid().nullable().optional(), // Link to existing ChildApplication
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  grade: GradeLevel,
  division: SchoolDivision,
  isEligibleForAid: z.boolean().default(true), // False for nursery/kindergarten
  tuitionAmount: z.number().nonnegative(),
  feesAmount: z.number().nonnegative().default(0), // Not covered by scholarship
});
export type YstStudentEntry = z.infer<typeof YstStudentEntrySchema>;
